import {
    parsePhoneNumberFromString,
    AsYouType,
    getCountries,
    getCountryCallingCode,
    getExampleNumber,
} from "libphonenumber-js";
import phoneExamples from "libphonenumber-js/examples.mobile.json";

const DEFAULT_COUNTRY = "BR";

export function callingCodeFor(iso = DEFAULT_COUNTRY) {
    try {
        return getCountryCallingCode(iso);
    } catch (e) {
        return "55";
    }
}

export function exampleNationalFor(iso = DEFAULT_COUNTRY) {
    try {
        const example = getExampleNumber(iso, phoneExamples);
        if (example) {
            return example.formatNational();
        }
    } catch (e) {
        // ignore
    }
    return "";
}

function flagFor(iso) {
    return iso
        .toUpperCase()
        .replace(/./g, (char) =>
            String.fromCodePoint(char.charCodeAt(0) + 127397)
        );
}

export function countryOptions(locale = "pt-BR") {
    let names = null;
    if (typeof Intl !== "undefined" && Intl.DisplayNames) {
        try {
            names = new Intl.DisplayNames([locale], { type: "region" });
        } catch (e) {
            names = new Intl.DisplayNames(["en"], { type: "region" });
        }
    }
    return getCountries()
        .map((iso) => {
            const dialCode = getCountryCallingCode(iso);
            const name = (names && names.of(iso)) || iso;
            const flag = flagFor(iso);
            return {
                iso,
                dialCode,
                name,
                flag,
                label: `${flag} ${name} (+${dialCode})`,
            };
        })
        .sort((a, b) => a.name.localeCompare(b.name, locale));
}

export function nationalDigitLimit(iso = DEFAULT_COUNTRY) {
    try {
        const example = getExampleNumber(iso, phoneExamples);
        if (example && example.nationalNumber) {
            return String(example.nationalNumber).length;
        }
    } catch (e) {
        // ignore
    }
    return 0;
}

export function processTypedNumber(iso, digitsOnly) {
    const limit = nationalDigitLimit(iso);
    let digits = String(digitsOnly || "").replace(/\D/g, "");
    if (limit && digits.length > limit) {
        digits = digits.slice(0, limit);
    }

    const formatter = new AsYouType(iso);
    const national = formatter.input(digits);
    const phone = formatter.getNumber();

    return {
        national,
        digits,
        e164: phone ? phone.number : `+${callingCodeFor(iso)}${digits}`,
        isValid: phone ? phone.isValid() : false,
        isoCode: phone ? phone.country : iso,
    };
}

export function parseFullNumber(raw, fallbackIso = DEFAULT_COUNTRY) {
    const formatter = new AsYouType();
    formatter.input(String(raw || ""));
    const phone = formatter.getNumber();
    if (phone) {
        const iso = phone.country || fallbackIso;
        const { national } = processTypedNumber(iso, phone.nationalNumber || "");
        return { national, isoCode: iso };
    }
    const digits = String(raw || "").replace(/\D/g, "");
    const { national } = processTypedNumber(fallbackIso, digits);
    return { national, isoCode: fallbackIso };
}

export function toE164(value, defaultCountry = DEFAULT_COUNTRY) {
    if (!value) {
        return null;
    }
    const raw = String(value).trim();
    try {
        const phone = parsePhoneNumberFromString(
            raw,
            raw.startsWith("+") ? undefined : defaultCountry
        );
        if (phone) {
            return phone.number;
        }
    } catch (e) {
        // ignore
    }
    return null;
}

export function isValidPhone(value, defaultCountry = DEFAULT_COUNTRY) {
    if (!value) {
        return false;
    }
    const raw = String(value).trim();
    try {
        const phone = parsePhoneNumberFromString(
            raw,
            raw.startsWith("+") ? undefined : defaultCountry
        );
        return !!phone && phone.isValid();
    } catch (e) {
        return false;
    }
}
